import { useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";

/**
 * Personal Hub: household income/expense baseline + per-debt rows + the
 * derived debt-payoff comparison.
 *
 * Numbers are nullable on purpose. The UI shows "—" with CTAs when missing.
 */
export interface PersonalIncome {
  id: string;
  ownerUserId: string;
  monthlyW2NetTakeHome: number | null;
  monthlyW2Gross: number | null;
  spouseMonthlyNetTakeHome: number | null;
  monthlyFixedExpenses: number | null;
  monthlyVariableExpenses: number | null;
  emergencyFundBalance: number | null;
  emergencyFundTargetMonths: number | null;
  employerPlanYtdContribution: number | null;
  employerOffersHdhpHsa: boolean | null;
  employerPlanSupportsMegaBackdoorRoth: boolean | null;
  notes: string;
  /** Derived on the server (TotalMonthlyNet - fixed - variable). */
  totalMonthlyNet: number | null;
  monthlySurplus: number | null;
}

export type DebtType =
  | "Other"
  | "CreditCard"
  | "Vehicle"
  | "StudentLoan"
  | "PersonalLoan"
  | "Mortgage"
  | "Heloc"
  | "Medical"
  | "BuyNowPayLater";

export interface Debt {
  id: string;
  ownerUserId: string;
  name: string;
  type: DebtType;
  currentBalance: number;
  /** Annual percentage rate as a decimal (0.2299 = 22.99%). */
  apr: number;
  minimumMonthlyPayment: number | null;
  actualMonthlyPayment: number | null;
  originalAmount: number | null;
  originationDate: string | null;
  vehicleYear: number | null;
  vehicleMake: string | null;
  vehicleModel: string | null;
  vehicleGvwrLb: number | null;
  vehicleFmv: number | null;
  estimatedBusinessUsePct: number | null;
  isBusinessConvertible: boolean;
  /** Optional FK to a PersonalAsset that secures this debt (Phase B). */
  securedByAssetId: string | null;
  notes: string;
}

// Keep these values identical to FireSoft.Domain.Personal.PersonalAssetType.
// ASP.NET binds enums from these strings before an asset endpoint is invoked.
export const PERSONAL_ASSET_TYPES = [
  "Other",
  "Vehicle",
  "PrimaryResidence",
  "RentalProperty",
  "Brokerage",
  "RothIra",
  "TraditionalIra",
  "Roth401k",
  "Traditional401k",
  "Hsa",
  "EducationSavings529",
  "Savings",
  "Checking",
  "Crypto",
  "PreciousMetals",
  "Collectible",
  "BusinessOwnership",
] as const;

export type PersonalAssetType = (typeof PERSONAL_ASSET_TYPES)[number];

export interface PersonalAsset {
  id: string;
  ownerUserId: string;
  name: string;
  type: PersonalAssetType;
  currentValue: number;
  purchasePrice: number | null;
  acquisitionDate: string | null;
  isTaxAdvantagedAccount: boolean;
  institution: string | null;
  vehicleYear: number | null;
  vehicleMake: string | null;
  vehicleModel: string | null;
  vehicleGvwrLb: number | null;
  estimatedBusinessUsePct: number | null;
  isBusinessConvertible: boolean;
  notes: string;
}

export interface NetWorthBucket {
  type: string;
  total: number;
  count: number;
}

export interface NetWorthSnapshot {
  netWorth: number;
  liquidNetWorth: number;
  totalAssets: number;
  totalDebts: number;
  taxableAssets: number;
  taxAdvantagedAssets: number;
  realEstateAssets: number;
  vehicleEquity: number;
  byAssetType: NetWorthBucket[];
  byDebtType: NetWorthBucket[];
  isEmpty: boolean;
}

export interface PerDebtPayoffSummary {
  id: string;
  name: string;
  type: string;
  startingBalance: number;
  apr: number;
  paidOffMonth: number | null;
  totalInterest: number;
  totalPaid: number;
}

export interface DebtPayoffPlan {
  strategy: "Avalanche" | "Snowball" | "BusinessConvertHybrid";
  monthsToDebtFree: number | null;
  totalInterestPaid: number;
  totalPaid: number | null;
  negativeCashFlow: boolean;
  perDebt: PerDebtPayoffSummary[];
  estimatedTaxSavings: number | null;
  section179Deduction: number | null;
}

export interface DebtPayoffComparison {
  isBlocked: boolean;
  totalPrincipal: number;
  monthlyAvailable: number | null;
  avalanche: DebtPayoffPlan;
  snowball: DebtPayoffPlan;
  businessConvertHybrid: DebtPayoffPlan | null;
  missingInputs: Array<{ debtId: string | null; label: string; why: string }>;
  notes: string[];
}

export interface PayoffPlanResponse {
  comparison: DebtPayoffComparison;
  inputs: {
    monthlyAvailable: number | null;
    monthlyAvailableSource: "Override" | "PersonalIncomeSurplus" | "Missing";
    marginalRate: number | null;
    marginalRateBlocked: boolean;
  };
}

const INCOME_KEY = ["personal", "income"];
const DEBTS_KEY = ["personal", "debts"];
const PAYOFF_KEY = ["personal", "payoff"];
const ASSETS_KEY = ["personal", "assets"];
const NET_WORTH_KEY = ["personal", "net-worth"];

export const PERSONAL_MUTATION_INVALIDATIONS = {
  income: [INCOME_KEY, PAYOFF_KEY, ["tax-plays"]],
  debt: [DEBTS_KEY, PAYOFF_KEY, NET_WORTH_KEY],
  asset: [ASSETS_KEY, NET_WORTH_KEY],
} as const;

export function invalidatePersonalQueries(
  queryClient: Pick<QueryClient, "invalidateQueries">,
  queryKeys: readonly (readonly unknown[])[],
) {
  for (const queryKey of queryKeys) {
    void queryClient.invalidateQueries({ queryKey });
  }
}

export function usePersonalIncome() {
  return useQuery({
    queryKey: INCOME_KEY,
    queryFn: async (): Promise<PersonalIncome> => {
      const r = await api().get<PersonalIncome>("/api/personal/income");
      return r.data;
    },
  });
}

export function useUpdatePersonalIncome() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: Partial<PersonalIncome>) => {
      const r = await api().put<PersonalIncome>("/api/personal/income", input);
      return r.data;
    },
    onSuccess: () => {
      invalidatePersonalQueries(qc, PERSONAL_MUTATION_INVALIDATIONS.income);
    },
  });
}

export function useDebts() {
  return useQuery({
    queryKey: DEBTS_KEY,
    queryFn: async (): Promise<Debt[]> => {
      const r = await api().get<Debt[]>("/api/personal/debts");
      return r.data;
    },
  });
}

export function useCreateDebt() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: Partial<Debt>) => {
      const r = await api().post<Debt>("/api/personal/debts", input);
      return r.data;
    },
    onSuccess: () => {
      invalidatePersonalQueries(qc, PERSONAL_MUTATION_INVALIDATIONS.debt);
    },
  });
}

export function useUpdateDebt() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...input }: Partial<Debt> & { id: string }) => {
      const r = await api().put<Debt>(`/api/personal/debts/${id}`, input);
      return r.data;
    },
    onSuccess: () => {
      invalidatePersonalQueries(qc, PERSONAL_MUTATION_INVALIDATIONS.debt);
    },
  });
}

export function useDeleteDebt() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api().delete(`/api/personal/debts/${id}`);
    },
    onSuccess: () => {
      invalidatePersonalQueries(qc, PERSONAL_MUTATION_INVALIDATIONS.debt);
    },
  });
}

// ----- Personal Assets -----
export function usePersonalAssets() {
  return useQuery({
    queryKey: ASSETS_KEY,
    queryFn: async (): Promise<PersonalAsset[]> => {
      const r = await api().get<PersonalAsset[]>("/api/personal/assets");
      return r.data;
    },
  });
}

export function useCreatePersonalAsset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: Partial<PersonalAsset>) => {
      const r = await api().post<PersonalAsset>("/api/personal/assets", input);
      return r.data;
    },
    onSuccess: () => {
      invalidatePersonalQueries(qc, PERSONAL_MUTATION_INVALIDATIONS.asset);
    },
  });
}

export function useUpdatePersonalAsset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...input }: Partial<PersonalAsset> & { id: string }) => {
      const r = await api().put<PersonalAsset>(`/api/personal/assets/${id}`, input);
      return r.data;
    },
    onSuccess: () => {
      invalidatePersonalQueries(qc, PERSONAL_MUTATION_INVALIDATIONS.asset);
    },
  });
}

export function useDeletePersonalAsset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api().delete(`/api/personal/assets/${id}`);
    },
    onSuccess: () => {
      invalidatePersonalQueries(qc, PERSONAL_MUTATION_INVALIDATIONS.asset);
    },
  });
}

export function useNetWorth() {
  return useQuery({
    queryKey: NET_WORTH_KEY,
    queryFn: async (): Promise<NetWorthSnapshot> => {
      const r = await api().get<NetWorthSnapshot>("/api/personal/net-worth");
      return r.data;
    },
  });
}

export function usePayoffPlan(extraMonthlyOverride?: number | null) {
  return useQuery({
    queryKey: [...PAYOFF_KEY, extraMonthlyOverride ?? null],
    queryFn: async (): Promise<PayoffPlanResponse> => {
      const r = await api().get<PayoffPlanResponse>("/api/personal/payoff-plan", {
        params: extraMonthlyOverride != null ? { extraMonthlyOverride } : undefined,
      });
      return r.data;
    },
  });
}
